import { useEffect, useRef, useState, type ReactNode } from 'react'

import { buzz, useReducedMotion } from '../shell/motion'

// ============================================================================
// V10 BOARD 21 · PULL TO SYNC — the Day screen's pull. The same job as PullToRefresh (a real sync
// of every connector that can sync on demand), drawn and tuned the V10 way:
//   · the PAGE moves, at 0.55 of the finger, up to 140px; a 44px card disc rides in the gap;
//   · past 70px it is ARMED: a haptic tick, and the disc's accent arc is full;
//   · release armed: the page holds at 64px while the disc spins and the sync runs, then a second
//     tick, the page springs back, and the caller opens the sync report.
//
// THE FINGER IS TRACKED OUTSIDE REACT. Moves write the page's transform and the disc's position
// straight onto their elements; React hears about the pull twice — when a sync starts and when it
// ends. Touch is read with native listeners so the move can be `preventDefault`ed once it is ours
// (a pull at the top of a list would otherwise also be the browser's overscroll); a mouse uses
// pointer events, for the desktop browser.
//
// School and Reminders keep the older PullToRefresh until their own V10 phases.
// ============================================================================

export const PULL_RESISTANCE = 0.55
export const PULL_ARM_PX = 70
export const PULL_MAX_PX = 140
/** Where the page holds while the sync runs (board 21). */
export const PULL_HOLD_PX = 64
/** Movement before the direction is decided — a sideways drag is never a pull. */
const DECIDE_PX = 8

/** How far the page has moved for a finger `dy` px below where it went down. */
export function pullDistance(dy: number): number {
  return Math.min(PULL_MAX_PX, Math.max(0, dy) * PULL_RESISTANCE)
}

export function pullArmed(pull: number): boolean {
  return pull > PULL_ARM_PX
}

/** The disc's arc, as a share of the circle: full at the arm point. */
export function pullArc(pull: number): number {
  return Math.min(100, Math.round((pull / PULL_ARM_PX) * 100))
}

export function PullToSync({
  onSync,
  onSynced,
  label,
  children,
}: {
  /** Runs the real sync. Resolves when every connector has answered or refused. */
  onSync: () => Promise<void>
  /** After the page has settled back: the caller opens its report. */
  onSynced: () => void
  /** What is being checked, for a screen reader ("Checking Canvas and Gmail…"). */
  label: string
  children: ReactNode
}) {
  const reduced = useReducedMotion()
  const [syncing, setSyncing] = useState(false)
  const scroller = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  const disc = useRef<HTMLDivElement>(null)
  const arc = useRef<HTMLSpanElement>(null)
  const busy = useRef(false)
  const latest = useRef({ onSync, onSynced, reduced })
  latest.current = { onSync, onSynced, reduced }

  useEffect(() => {
    const root = scroller.current
    const page = content.current
    const spinner = disc.current
    const ring = arc.current
    if (!root || !page || !spinner || !ring) return

    let g: { x0: number; y0: number; zoom: number; decided: boolean; pull: number; armed: boolean } | null = null

    const paint = (pull: number, dragging: boolean) => {
      page.classList.toggle('m-pull-content', !dragging)
      page.style.transform = pull > 0 ? `translateY(${pull}px)` : ''
      spinner.style.opacity = pull > 6 ? '1' : '0'
      spinner.style.transform = `translateY(${pull / 2 - 22}px)`
      ring.style.setProperty('--m-arc', `${pullArc(pull)}%`)
    }

    const start = (x: number, y: number, target: EventTarget | null) => {
      if (busy.current || root.scrollTop > 0) return
      if (target instanceof Element && target.closest('[data-no-pull]')) return
      // The finger is in screen pixels and the page is drawn at the app's size setting (130% by
      // default): divide by it, so the page moves at 0.55 of the finger on screen, as board 21 says.
      const rect = root.getBoundingClientRect().width
      const zoom = root.offsetWidth > 0 && rect > 0 ? rect / root.offsetWidth : 1
      g = { x0: x, y0: y, zoom, decided: false, pull: 0, armed: false }
    }

    /** True when the move is now the pull's, so the browser should not also scroll. */
    const move = (x: number, y: number): boolean => {
      if (!g) return false
      const dx = x - g.x0
      const dy = y - g.y0
      if (!g.decided) {
        if (Math.hypot(dx, dy) < DECIDE_PX) return false
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || root.scrollTop > 0) {
          g = null
          return false
        }
        g.decided = true
      }
      g.pull = pullDistance(dy / g.zoom)
      const armed = pullArmed(g.pull)
      if (armed && !g.armed) buzz(8)
      g.armed = armed
      paint(g.pull, true)
      return true
    }

    const end = () => {
      const was = g
      g = null
      if (!was?.decided) return
      if (!was.armed) {
        paint(0, false)
        return
      }
      busy.current = true
      setSyncing(true)
      paint(PULL_HOLD_PX, false)
      ring.style.setProperty('--m-arc', '30%')
      void latest.current
        .onSync()
        .catch(() => {
          // The caller turns a refusal into words on the report; nothing to add here.
        })
        .finally(() => {
          buzz(8)
          paint(0, false)
          busy.current = false
          setSyncing(false)
          latest.current.onSynced()
        })
    }

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0]
      if (e.touches.length === 1 && t) start(t.clientX, t.clientY, e.target)
    }
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0]
      if (t && move(t.clientX, t.clientY) && e.cancelable) e.preventDefault()
    }
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button === 0) start(e.clientX, e.clientY, e.target)
    }
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') move(e.clientX, e.clientY)
    }
    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') end()
    }

    root.addEventListener('touchstart', onTouchStart, { passive: true })
    root.addEventListener('touchmove', onTouchMove, { passive: false })
    root.addEventListener('touchend', end)
    root.addEventListener('touchcancel', end)
    root.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    return () => {
      root.removeEventListener('touchstart', onTouchStart)
      root.removeEventListener('touchmove', onTouchMove)
      root.removeEventListener('touchend', end)
      root.removeEventListener('touchcancel', end)
      root.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, [])

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={disc}
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[8px] z-[1] -ml-[22px] flex size-[44px] items-center justify-center rounded-full bg-m-card opacity-0"
        style={{ boxShadow: 'var(--m-shadow-spin)' }}
      >
        <span ref={arc} className={`m-pull-arc flex size-[28px] items-center justify-center rounded-full ${syncing && !reduced ? 'm-spin' : ''}`}>
          <span className="size-[20px] rounded-full bg-m-card" />
        </span>
      </div>
      {syncing && (
        <span role="status" className="sr-only">
          {label}
        </span>
      )}
      <div ref={scroller} className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain">
        <div ref={content} className="m-pull-content flex flex-col">
          {children}
        </div>
      </div>
    </div>
  )
}
